// @ts-nocheck
import { TextInput, MD3DarkTheme as DefaultTheme, configureFonts } from "react-native-paper";
import { TEXT_INPUT_FONTSIZE } from "../../utils/Typography";
import { View, Text } from "react-native";
import ITextInputProps from "../../models/ITextInputProps";
import { white_smoke, light_black, quick_silver, mid_blue, errorColor } from "../../StyleHelpers";
import { useRef, useEffect, useState } from "react";
import { globalAny } from "../../utils/Utils";
import { StyledText, StyledTextError, StyledView, StyledInput, fontConfig, StyledErrorContent } from "./styled";
import Hide from "../../assets/Icons/Hide.svg";
import Show from "../../assets/Icons/Show.svg";
import { Icon } from "../../Types";
import useGenericContentStore from "../../store/genericContent.store";

const TextInputVinfast = (props: ITextInputProps) => {
	const textRef = useRef<any>(null);
	const [counter, setCounter] = useState(25 - (props.value?.length || 0));
	const { isNarrow } = useGenericContentStore((state) => ({ isNarrow: state.isNarrow }));

	/**
	 * Handles changes to the text input value.
	 * - Updates character counter if enabled.
	 * - Capitalizes the first character if the prop is set.
	 * - Passes the updated text to the parent via `onChangeText`.
	 *
	 * @param text - The new text input value.
	 */
	const handleChangeText = (text: string) => {
		if (props.isCounterLabel) {
			setCounter(25 - text.length);
		}
		const finalText = props.capitalizeFirstCharacter ? text.charAt(0).toUpperCase() + text.slice(1) : text;
		props.onChangeText?.(finalText);
	};

	/**
	 * Renders a custom label for the TextInput component.
	 * - Applies consistent background color.
	 * - Increases font size and line height for better visibility.
	 * - Uses white text color and left margin for spacing.
	 */
	const renderLabel = (
		<View style={{ backgroundColor: light_black }}>
			<Text
				style={{
					fontSize: isNarrow ? 21 : 27,
					lineHeight: isNarrow ? 25 : 50,
					fontWeight: "500",
					marginLeft: 7,
					color: white_smoke,
					backgroundColor: light_black,
				}}
			>
				{props.label}
			</Text>
		</View>
	);

	/**
	 * Conditionally renders an icon inside the TextInput component.
	 * - Shows 'Show' or 'Hide' icon based on `props.inputIcon` value.
	 * - Executes `onPressInputIcon` when the icon is pressed.
	 * - Styles the icon with margin, color, and size.
	 */
	const renderIcon = props.inputIcon && (
		<TextInput.Icon
			icon={props.inputIcon === Icon.eye ? Show : Hide}
			onPress={props.onPressInputIcon}
			style={{ marginTop: 15, marginRight: 20 }}
			iconColor={white_smoke}
			size={50}
		/>
	);

	/**
	 * Custom theme configuration for the TextInput component.
	 * - Extends the default dark theme.
	 * - Applies custom font configuration.
	 * - Overrides the error color.
	 */
	const theme = {
		...DefaultTheme,
		fonts: configureFonts({ config: fontConfig, isV3: true }),
		colors: { ...DefaultTheme.colors, error: errorColor },
	};

	/**
	 * Automatically focuses the text input on mount if `autoFocus` prop is true.
	 */
	useEffect(() => {
		if (props.autoFocus && textRef.current?.focus) {
			textRef.current.focus();
		}
	}, [props.autoFocus]);

	return (
		<StyledView addLeftSpacing={props.addLeftSpacing} addRightSpacing={props.addRightSpacing}>
			<StyledInput
				ref={textRef}
				keyboardType={props.keyboardType}
				disabled={props.disabled}
				nativeID={props.nativeID}
				className={props.className}
				mode="outlined"
				activeOutlineColor={mid_blue}
				outlineColor={props.hasValue ? white_smoke : quick_silver}
				error={props.error}
				autoComplete="off"
				placeholder={props.placeholder}
				secureTextEntry={props.passwordField}
				value={props.value}
				outlineStyle={{ borderRadius: 16, borderWidth: 2 }}
				fontSize={TEXT_INPUT_FONTSIZE}
				label={renderLabel}
				onKeyPress={props.onKeyPress}
				onChangeText={handleChangeText}
				textColor={white_smoke}
				onBlur={props.onBlur}
				onFocus={props.onFocus}
				right={renderIcon}
				theme={theme}
				render={props.render}
				maxLength={props.maxLength}
				onEndEditing={props.onEndEditing}
				onSubmitEditing={props.onSubmitEditing}
				onChange={props.onChange}
				selectTextOnFocus={true}
				returnKeyType="done"
				height={isNarrow ? 55 : props.height}
			/>

			{!props.isHideError ? (
				<StyledErrorContent isNarrow={isNarrow}>
					{props.isCounterLabel && <StyledText>{`${Math.max(counter, 0)} ${globalAny.language.characters_left}`}</StyledText>}
					{props.error && <StyledTextError>{props.errorMessage}</StyledTextError>}
				</StyledErrorContent>
			) : (
				<></>
			)}
		</StyledView>
	);
};

export default TextInputVinfast;
